import {Injectable} from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  CanLoad,
  Route,
  Router,
  RouterStateSnapshot,
  UrlSegment,
  UrlTree
} from '@angular/router';
import {Observable, throwError} from 'rxjs';
import {catchError, map, take} from 'rxjs/operators';
import {AuthService} from './auth.service';
import {ApiService} from './api.service';
import {CommonUtils} from "../utils/common";

/**
 * Guard zarządzający dostępem do modułu wyników
 * badań laboratoryjnych (Lab Test Data).
 * Weryfikuje uprawnienia użytkownika do przeglądania wyników
 * badań laboratoryjnych, opierając się na walidacji API po stronie serwera.
 */
@Injectable({
  providedIn: 'root'
})
export class LabTestDataAccessGuard implements CanLoad, CanActivate {
  constructor(
    private authService: AuthService,
    private router: Router,
    private apiService: ApiService,
    public commonUtils: CommonUtils
  ) {
  }

  /**
   * Blokuje lub zezwala na asynchroniczne załadowanie (Lazy Loading) modułu wyników laboratoryjnych.
   * Funkcja ochronna optymalizująca bezpieczeństwo i oszczędzająca transfer – weryfikuje dostęp
   * poprzez zapytanie POST przed inicjalizacją pobierania kodu JavaScript modułu z serwera.
   * W przypadku błędu 401 (Unauthorized), przerywa sesję i przekierowuje do logowania.
   *
   * @param route Konfiguracja chronionej ścieżki.
   * @param segments Segmenty adresu URL.
   * @returns Observable emitujący wartość logiczną (true w przypadku sukcesu).
   */
  canLoad(
    route: Route,
    segments: UrlSegment[]
  ): Observable<boolean> | Promise<boolean> | boolean {
    return this.apiService.post('/dashboard/lab-test-data-access', {}).pipe(take(1), catchError(err => {
      if (err.status === 401) {
        this.router.navigate(['signin']).then(() => {
          this.authService.logout();
        });
      }
      return throwError(err);
    }), map(response => {
      return true;
    }));
  }

  /**
   * Weryfikuje uprawnienia przed aktywacją i wyrenderowaniem komponentu powiązanego
   * z wynikami badań laboratoryjnych. Przechwytuje wygaśnięcie tokena (401),
   * a w pozostałych przypadkach odmowy dostępu deleguje obsługę komunikatu
   * (Toast) do serwisów narzędziowych.
   *
   * @param route Migawka aktywowanej ścieżki.
   * @param state Migawka stanu routera.
   * @returns Observable emitujący wartość logiczną lub drzewo routingu (UrlTree).
   */
  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    return this.apiService.post('/dashboard/lab-test-data-access', {}).pipe(take(1), catchError(err => {
      if (err.status === 401) {
        this.router.navigate(['signin']).then(() => {
          this.authService.logout();
        });
      }
      return throwError(err);
    }), map(response => {
      return this.commonUtils.handleHttpResponseShowInfoToast(response, '', 'Nie masz uprawnień do tej strony')
    }));
  }

}
